import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from '@/components/ui/badge';
import { Bell, Settings, TrendingUp, UserCheck, ShoppingCart, Clock, Eye, Trash2, ExternalLink, Mail } from 'lucide-react';
import { AlertPreference } from '@/api/entities';
import { Alert } from '@/api/entities';
import { alertsApi } from '@/lib/api/alerts';
import { useUser, useCurrentTenant, useTenantCapabilities } from '@/contexts/UserContext';
import { resolveEffectiveTenant, getEffectiveTenant } from '@/lib/tenantUtils';
import { fetchMyTendersConfig } from '@/lib/api/myTenders';
import { Procurement } from '@/api/entities';
import { MomentumScore } from '@/api/entities';
import { PersonaActivity } from '@/api/entities';
import { useToast } from '@/components/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { normalizeLiveDealCategories } from '@/lib/alertCategoryNames';

const CATEGORY_OPTIONS = [
  "Learning Management Systems (LMS)",
  "Student Information Systems (SIS)",
  "Virtual Classroom & Video-Conferencing",
  "Lecture Capture & Video CMS",
  "Academic Integrity / Plagiarism Detection",
  "Online Exam Platforms",
  "Remote Proctoring",
  "Feedback & Evaluation",
  "Student Success & Engagement",
  "Student Recruitment & Engagement CRM",
  "Digital Credentialing & Badging",
  "Adaptive Learning Courseware",
  "Digital Course Content & Inclusive Access",
  "AI & Virtual Assistants",
  "Virtual Labs & STEM Simulations",
  "Career Services & Employability",
  "Accessibility / Assistive-Tech Suites",
  "Campus Mobile ID & Commerce",
  "Research & Data Management Platforms",
  "Research Information Management (CRIS)",
  "Classroom Engagement & Polling",
  "XR / Immersive Learning Platforms",
  "e-Portfolio / Comprehensive Learner Record",
  "Timetabling, Scheduling & Space Management",
  "Student Housing & Accommodation Systems",
  "Student Payments & Financial Aid Platforms",
  "Integration & Data Platforms",
  "IT Service Management (ITSM) & Workflow Automation",
  "Cybersecurity & Compliance Platforms",
  "Library & Discovery Services / Reading List Systems",
  "Library Self-Service, RFID & Automated Handling",
  "Reading & Literacy Platforms",
  "Campus Safety & Security Apps",
  "Student Case Management & Retention CRM",
  "Faculty Information & Workload Systems",
  "Alumni & Advancement Systems",
  "Website Development (Education only)",
  "E-learning Content / Authoring / Learning Services",
  "IT Services (Education only)",
  "IT Hardware (Education only)",
  "Talent Management / HCM",
  "Lab Equipment (Education only)",
  "Robotics, Automation & Industry 4.0 Training",
  "Language Learning",
  "Marketing",
  "Marking / Grading Platforms",
  "Cloud Services (Education only)",
  "OMR & Document Scanning Hardware",
];
const SORTED_CATEGORY_OPTIONS = [...CATEGORY_OPTIONS].sort((a, b) =>
  a.localeCompare(b, undefined, { sensitivity: 'base' })
);

function getAlertCacheKey(userId, tenantId) {
  return `alert-settings-cache-v1-${userId || 'unknown'}-${tenantId || 'unknown'}`;
}

function readAlertCache(userId, tenantId) {
  try {
    const raw = sessionStorage.getItem(getAlertCacheKey(userId, tenantId));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    console.error("Error reading alert settings cache:", e);
    return null;
  }
}

function writeAlertCache(userId, tenantId, snapshot) {
  try {
    sessionStorage.setItem(
      getAlertCacheKey(userId, tenantId),
      JSON.stringify({ ...snapshot, cachedAt: Date.now() })
    );
  } catch (e) {
    console.error("Error writing alert settings cache:", e);
  }
}

/** HTML time inputs require HH:mm; API may return HH:mm:ss or other shapes. */
function normalizeTimeForInput(value) {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  const match = s.match(/^(\d{1,2}):(\d{2})/);
  if (match) {
    const hh = Math.min(23, Math.max(0, parseInt(match[1], 10)));
    const mm = Math.min(59, Math.max(0, parseInt(match[2], 10)));
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  return '';
}

function withNormalizedDndTimes(prefs) {
  if (!prefs) return prefs;
  return {
    ...prefs,
    dnd_start: normalizeTimeForInput(prefs.dnd_start) || prefs.dnd_start || '22:00',
    dnd_end: normalizeTimeForInput(prefs.dnd_end) || prefs.dnd_end || '08:00',
  };
}

// Helper to check if a string looks like a valid database ID
function isValidId(str) {
  if (!str || typeof str !== 'string') return false;
  
  // MongoDB ObjectId (24 hex chars)
  if (/^[a-f0-9]{24}$/i.test(str)) return true;
  
  // UUID format
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str)) return true;
  
  // Names typically contain dots, underscores as separators
  if (str.includes('.') || str.includes('_')) return false;
  
  // Skip if it looks like an email
  if (str.includes('@')) return false;
  
  // Generic alphanumeric ID (at least 15 chars, no special chars)
  if (/^[a-zA-Z0-9]+$/.test(str) && str.length >= 15) return true;
  
  return false;
}

// Tenant-specific default preferences
const TENANT_DEFAULTS = {
  smowl: {
    live_deal_categories: [
      "Remote Proctoring",
      "Online Exam Platforms"
    ],
    momentum_threshold: 4,
    persona_threshold: 4
  },
  surpass: {
    live_deal_categories: [
      "Online Exam Platforms",
      "Remote Proctoring",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  surpass_v2: {
    live_deal_categories: [
      "Online Exam Platforms",
      "Remote Proctoring",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  vretta: {
    live_deal_categories: [
      "Online Exam Platforms",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  funidata: {
    live_deal_categories: [
      "Student Information Systems (SIS)",
      "Faculty Information & Workload Systems"
    ],
    momentum_threshold: 4,
    persona_threshold: 4
  },
  akari: {
    live_deal_categories: [
      "Curriculum Enrichment & Curriculum Management Platforms"
    ],
    momentum_threshold: 4,
    persona_threshold: 4
  },
  gravyty: {
    live_deal_categories: [
      "Alumni & Advancement Systems",
      "Student Success & Engagement"
    ],
    momentum_threshold: 4,
    persona_threshold: 4
  },
  almentor: {
    live_deal_categories: [
      "E-learning Content / Authoring / Learning Services",
      "Digital Course Content & Inclusive Access"
    ],
    momentum_threshold: 4,
    persona_threshold: 4
  },
  infonetica: {
    live_deal_categories: [
      "Research & Data Management Platforms"
    ],
    momentum_threshold: 4,
    persona_threshold: 4
  },
  cayuse: {
    live_deal_categories: [
      "Research & Data Management Platforms"
    ],
    momentum_threshold: 4,
    persona_threshold: 4
  },
  uplanner: {
    live_deal_categories: [
      "Timetabling, Scheduling & Space Management",
      "Student Success & Engagement",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  edusign: {
    live_deal_categories: [
      "Timetabling, Scheduling & Space Management",
      "Feedback & Evaluation",
      "Student Success & Engagement",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  liaison: {
    live_deal_categories: [
      "Student Recruitment & Engagement CRM",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  fullfabric: {
    live_deal_categories: [
      "Student Recruitment & Engagement CRM",
      "Alumni & Advancement Systems",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  fullfabric_v2: {
    live_deal_categories: [
      "Student Recruitment & Engagement CRM",
      "Alumni & Advancement Systems",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  synap: {
    live_deal_categories: [
      "Feedback & Evaluation",
      "Online Exam Platforms",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  scantron: {
    live_deal_categories: [
      "Online Exam Platforms",
      "Feedback & Evaluation",
      "OMR & Document Scanning Hardware",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  instructure: {
    live_deal_categories: [
      "Learning Management Systems (LMS)",
    ],
    momentum_threshold: 4,
    persona_threshold: 4
  },
  clairelabs: {
    live_deal_categories: [],
    momentum_threshold: 4,
    persona_threshold: 4
  },
  demarque: {
    live_deal_categories: [],
    momentum_threshold: 4,
    persona_threshold: 4
  },
  demarque_v2: {
    live_deal_categories: [],
    momentum_threshold: 4,
    persona_threshold: 4
  },
  bibliotheca: {
    live_deal_categories: [
      "Library & Discovery Services / Reading List Systems",
      "Library Self-Service, RFID & Automated Handling",
    ],
    momentum_threshold: 4,
    persona_threshold: 4
  },
  edqwest: {
    live_deal_categories: [],
    momentum_threshold: 4,
    persona_threshold: 4
  },
  faria: {
    live_deal_categories: [],
    momentum_threshold: 4,
    persona_threshold: 4
  },
  calmedica: {
    live_deal_categories: [
      "Patient Engagement & Remote Monitoring",
      "Telemedicine",
    ],
    momentum_threshold: 4,
    persona_threshold: 4
  },
  diambra: {
    live_deal_categories: [],
    momentum_threshold: 4,
    persona_threshold: 4
  },
  seba: {
    live_deal_categories: [],
    momentum_threshold: 4,
    persona_threshold: 4
  },
  rumi: {
    live_deal_categories: [
      "Academic Integrity / Plagiarism Detection",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  studentcrm: {
    live_deal_categories: [
      "Student Recruitment & Engagement CRM",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  unit4: {
    live_deal_categories: [
      "Finance & ERP",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  blackboard: {
    live_deal_categories: [
      "Learning Management Systems (LMS)",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  constructor_tech: {
    live_deal_categories: [
      "Remote Proctoring",
      "Marking / Grading Platforms",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  // [2026-10-18] testwe — its three lens categories, labels as stored on deals.category.
  testwe: {
    live_deal_categories: [
      "Online Exam Platforms",
      "Remote Proctoring",
      "Marking / Grading Platforms",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  efekta: {
    live_deal_categories: [
      "Language Learning",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  jpik: {
    live_deal_categories: [
      "IT Hardware",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  positivo: {
    live_deal_categories: [
      "IT Hardware",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  feedbackfruits: {
    live_deal_categories: [
      "Marking / Grading Platforms",
      "Classroom Engagement & Polling",
      "Academic Integrity / Plagiarism Detection",
      "Online Exam Platforms",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
  feedbackfruits_v2: {
    live_deal_categories: [
      "Marking / Grading Platforms",
      "Classroom Engagement & Polling",
      "Academic Integrity / Plagiarism Detection",
      "Online Exam Platforms",
    ],
    momentum_threshold: 4,
    persona_threshold: 4,
  },
};

function getTenantDefaults(tenantId) {
  // Use .toLowerCase() for case-insensitive matching
  return TENANT_DEFAULTS[tenantId?.toLowerCase()] || TENANT_DEFAULTS.instructure;
}

const MOMENTUM_THRESHOLD_MAX = 6;
const PERSONA_THRESHOLD_MAX = 5;

function clampAlertThreshold(value, max) {
  const n = Number.parseInt(value, 10);
  if (!Number.isFinite(n)) return 1;
  return Math.min(max, Math.max(1, n));
}

export default function AlertSettings() {
  const contextUser = useUser();
  const { showCore } = useTenantCapabilities();
  const contextTenant = useCurrentTenant();
  const [preferences, setPreferences] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hydratedFromCache, setHydratedFromCache] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tenantId, setTenantId] = useState('instructure');
  const { toast } = useToast();
  const [dndForm, setDndForm] = useState({
    start: '22:00',
    end: '08:00',
    timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC',
  });
  const dndSaveTimerRef = useRef(null);
  const preferencesRef = useRef(null);
  preferencesRef.current = preferences;

  const [categoryGridCols, setCategoryGridCols] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches ? 2 : 1,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => setCategoryGridCols(mq.matches ? 2 : 1);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const categoryGridRows = useMemo(
    () => Math.ceil(SORTED_CATEGORY_OPTIONS.length / categoryGridCols),
    [categoryGridCols],
  );

  useEffect(() => {
    if (!contextUser) return;
    const effectiveTenantId = resolveEffectiveTenant(contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null, contextTenant, contextUser, 'instructure');
    const cached = readAlertCache(contextUser?.id, effectiveTenantId);
    if (cached?.preferences) {
      const cachedPrefs = withNormalizedDndTimes({
        ...cached.preferences,
        live_deal_categories: normalizeLiveDealCategories(cached.preferences.live_deal_categories),
      });
      setPreferences(cachedPrefs);
      setDndForm({
        start: cachedPrefs.dnd_start || '22:00',
        end: cachedPrefs.dnd_end || '08:00',
        timezone: cachedPrefs.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      setAlerts(cached.alerts || []);
      setTenantId(effectiveTenantId);
      setHydratedFromCache(true);
      setIsLoading(false);
    }
  }, [contextUser, contextTenant]);

  // [2026-11-01] The review reminder only exists where My Deals is switched on for the workspace.
  const [myDealsEnabled, setMyDealsEnabled] = useState(false);
  useEffect(() => {
    if (!tenantId) return undefined;
    let cancelled = false;
    fetchMyTendersConfig(tenantId)
      .then((cfg) => { if (!cancelled) setMyDealsEnabled(!!cfg?.enabled); })
      .catch(() => { if (!cancelled) setMyDealsEnabled(false); });
    return () => { cancelled = true; };
  }, [tenantId]);

  const loadData = useCallback(async () => {
    if (!contextUser) return;
    if (!hydratedFromCache) setIsLoading(true);
    
    try {
      const effectiveTenantId = resolveEffectiveTenant(contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null, contextTenant, contextUser, 'instructure');
      setTenantId(effectiveTenantId);
      
      console.log('🔔 AlertSettings: Loading for user:', contextUser.id, 'tenant:', effectiveTenantId);
      
      // Filter by BOTH user_id AND tenant_id
      let userPrefs = (await AlertPreference.filter({ 
        user_id: contextUser.id,
        tenant_id: effectiveTenantId 
      }))[0];
      
      if (!userPrefs) {
        const tenantDefaults = getTenantDefaults(effectiveTenantId);
        const defaultPrefs = {
          user_id: contextUser.id,
          tenant_id: effectiveTenantId,  // Include tenant_id in create
          email_digest_on: true,  // Enabled by default for new alert settings
          live_deal_on: true,
          live_deal_categories: tenantDefaults.live_deal_categories,
          momentum_on: true,
          momentum_threshold: tenantDefaults.momentum_threshold,
          persona_on: true,
          persona_threshold: tenantDefaults.persona_threshold,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          dnd_start: "22:00",
          dnd_end: "08:00"
        };
        userPrefs = await AlertPreference.create(defaultPrefs);
      }

      userPrefs = withNormalizedDndTimes({
        ...userPrefs,
        live_deal_categories: normalizeLiveDealCategories(userPrefs.live_deal_categories),
      });
      setDndForm({
        start: userPrefs.dnd_start || '22:00',
        end: userPrefs.dnd_end || '08:00',
        timezone: userPrefs.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      
      const existingAlerts = await alertsApi.list(contextUser.id, effectiveTenantId, 50);

      // 🔗 Enrich alerts with URLs from their linked entities.
      // NOTE: Entity-layer calls (Procurement.filter, etc.) may not be fully tenant-safe.
      // Alert list is tenant-scoped; linked URL enrichment is a follow-up risk—test under admin tenant switching.
      const alertsWithUrls = await Promise.all(
        existingAlerts.map(async (alert) => {
          if (alert.source_url) return alert; // already enriched

          // Skip if entity_id doesn't look like a valid database ID
          if (!alert.entity_id || !isValidId(alert.entity_id)) {
            return alert;
          }

          let sourceUrl = null;

          try {
            if (alert.type === 'live_deal') {
              const deals = await Procurement.filter({ id: alert.entity_id });
              if (deals.length > 0) sourceUrl = deals[0].source_url;
            } else if (alert.type === 'momentum') {
              const scores = await MomentumScore.filter({ id: alert.entity_id });
              if (scores.length > 0) sourceUrl = scores[0].source_url;
            } else if (alert.type === 'persona') {
              const activities = await PersonaActivity.filter({ id: alert.entity_id });
              if (activities.length > 0) {
                sourceUrl = activities[0].source_url || activities[0].full_url;
              }
            }
          } catch (err) {
            console.error('Error enriching alert with URL', alert.id, err);
          }

          return sourceUrl ? { ...alert, source_url: sourceUrl } : alert;
        })
      );
      
      console.log('📊 AlertSettings loaded:', {
        total: alertsWithUrls.length,
        withUrls: alertsWithUrls.filter(a => a.source_url).length
      });
      
      setPreferences(userPrefs);
      setAlerts(alertsWithUrls);
      
      writeAlertCache(contextUser.id, effectiveTenantId, {
        preferences: userPrefs,
        alerts: alertsWithUrls,
        user: contextUser
      });
      
      // 🔔 Also cache for Home page (localStorage persists across sessions)
      if (contextUser?.id) {
        localStorage.setItem(`alert-preferences-cache-${contextUser.id}-${effectiveTenantId}`, JSON.stringify(userPrefs));
        console.log('🔔 AlertSettings: Cached preferences to localStorage for user:', contextUser.id, 'tenant:', effectiveTenantId, userPrefs);
      }
      
    } catch (error) {
      console.error("Error loading alert data:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load alert settings."
      });
    } finally {
      setIsLoading(false);
      setHydratedFromCache(true);
    }
  }, [toast, hydratedFromCache, contextUser, contextTenant]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const savePreferences = async (updatedPrefs, options = {}) => {
    const { silent = false } = options;
    if (!updatedPrefs?.id) {
      if (!silent) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Alert preferences are still loading. Please try again in a moment.',
        });
      }
      return;
    }
    const payload = withNormalizedDndTimes({
      ...updatedPrefs,
      live_deal_categories: normalizeLiveDealCategories(updatedPrefs.live_deal_categories || []),
    });
    const previousPrefs = preferencesRef.current;
    preferencesRef.current = payload;
    setPreferences(payload);
    setIsSaving(true);
    try {
      console.log('🔔 SAVE: Starting save with:', {
        prefId: payload.id,
        categories: payload.live_deal_categories
      });
      
      const saved = await AlertPreference.update(payload.id, payload);
      
      console.log('🔔 SAVE: Response from API:', {
        savedId: saved?.id,
        savedCategories: saved?.live_deal_categories
      });
      
      setPreferences(withNormalizedDndTimes(saved));
      setDndForm({
        start: normalizeTimeForInput(saved?.dnd_start) || payload.dnd_start,
        end: normalizeTimeForInput(saved?.dnd_end) || payload.dnd_end,
        timezone: saved?.timezone || payload.timezone,
      });
      
      const effectiveTenantId = resolveEffectiveTenant(contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null, contextTenant, contextUser, 'instructure');
      
      // Broadcast to Home
      window.dispatchEvent(
        new CustomEvent('alert-preferences-updated', { detail: saved })
      );
      
      writeAlertCache(contextUser.id, effectiveTenantId, {
        preferences: saved,
        alerts,
        user: contextUser
      });
      
      // Update tenant-specific cache
      if (contextUser?.id) {
        localStorage.setItem(`alert-preferences-cache-${contextUser.id}-${effectiveTenantId}`, JSON.stringify(saved));
        localStorage.removeItem(`last-alert-prefs-load-${contextUser.id}-${effectiveTenantId}`);
      }
      
      if (!silent) {
        toast({
          title: "Settings saved",
          description: "Your alert preferences have been updated."
        });
      }
    } catch (error) {
      if (previousPrefs) {
        preferencesRef.current = previousPrefs;
        setPreferences(previousPrefs);
        setDndForm({
          start: previousPrefs.dnd_start || '22:00',
          end: previousPrefs.dnd_end || '08:00',
          timezone: previousPrefs.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
      }
      console.error("Error saving preferences:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save alert preferences."
      });
    }
    setIsSaving(false);
  };

  const scheduleDndSave = useCallback((nextDnd) => {
    if (dndSaveTimerRef.current) clearTimeout(dndSaveTimerRef.current);
    dndSaveTimerRef.current = setTimeout(() => {
      const base = preferencesRef.current;
      if (!base?.id || isLoading) return;
      savePreferences(
        {
          ...base,
          dnd_start: nextDnd.start,
          dnd_end: nextDnd.end,
          timezone: nextDnd.timezone,
        },
        { silent: true }
      );
    }, 700);
  }, [isLoading]);

  useEffect(() => () => {
    if (dndSaveTimerRef.current) clearTimeout(dndSaveTimerRef.current);
  }, []);

  const handleAlertClick = async (alert) => {
    try {
      const now = new Date().toISOString();
      await alertsApi.update(alert.id, { read_at: now }, tenantId);
      const updatedAlerts = alerts.map(a => 
        a.id === alert.id ? { ...a, read_at: now } : a
      );
      setAlerts(updatedAlerts);
      writeAlertCache(contextUser?.id, tenantId, { preferences, alerts: updatedAlerts, user: contextUser });

      console.log('✅ AlertSettings: Marked alert as read, broadcasting event');
      
      // 🔔 Notify bell & counters
      window.dispatchEvent(new CustomEvent('alerts-updated'));
    } catch (error) {
      console.error("Error marking alert as read:", error);
    }

    if (alert.source_url) {
      window.open(alert.source_url, '_blank');
    }
  };

  const deleteAlert = async (alertId, event) => {
    if (event) event.stopPropagation();
    
    try {
      const deleted = alerts.find(a => a.id === alertId);
      await alertsApi.delete(alertId, tenantId);
      const updatedAlerts = alerts.filter(alert => alert.id !== alertId);
      setAlerts(updatedAlerts);
      
      writeAlertCache(contextUser?.id, tenantId, { preferences, alerts: updatedAlerts, user: contextUser });
      
      // 🔔 If it was unread, reduce bell count
      if (deleted && !deleted.read_at) {
        console.log('✅ AlertSettings: Deleted unread alert, broadcasting event');
        window.dispatchEvent(new CustomEvent('alerts-updated'));
      }

      toast({ title: "Alert deleted" });
    } catch (error) {
      console.error("Error deleting alert:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete alert."
      });
    }
  };

  if (!contextUser) {
    return (
      <div className="bg-white min-h-screen">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (isLoading && !hydratedFromCache) {
    return (
      <div className="bg-white min-h-screen">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!preferences) {
    return (
      <div className="bg-white min-h-screen">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <p className="text-center text-[#5C6B7A]">Loading preferences...</p>
        </div>
      </div>
    );
  }

  const visibleAlerts = showCore
    ? alerts
    : alerts.filter((alert) => alert.type === 'live_deal' || !alert.type);
  const unreadAlerts = visibleAlerts.filter(alert => !alert.read_at);
  const getAlertIcon = (type) => {
    switch (type) {
      case 'live_deal': return <ShoppingCart className="w-4 h-4 text-blue-500" />;
      case 'momentum': return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'persona': return <UserCheck className="w-4 h-4 text-purple-500" />;
      default: return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#0A1A2F] flex items-center gap-3">
            <Bell className="w-8 h-8 text-[#00C2C2]" />
            Alert Center
          </h1>
          <p className="text-lg text-[#5C6B7A]">Manage your notifications and stay informed</p>
        </div>

        <Tabs defaultValue="alerts">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="alerts" className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              My Alerts {unreadAlerts.length > 0 && <Badge className="ml-2">{unreadAlerts.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Preferences
            </TabsTrigger>
          </TabsList>

          <TabsContent value="alerts" className="mt-6">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Recent Alerts
                  {unreadAlerts.length > 0 && (
                    <Badge className="bg-red-100 text-red-800">
                      {unreadAlerts.length} unread
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {visibleAlerts.length === 0 ? (
                  <div className="text-center py-8 text-[#5C6B7A]">
                    <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No alerts yet</p>
                    <p className="text-sm">You'll see notifications here as they arrive</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {visibleAlerts.map(alert => (
                      <div
                        key={alert.id}
                        className={`p-4 rounded-lg border transition-colors ${
                          alert.read_at ? 'bg-gray-50 border-gray-200' : 'bg-blue-50 border-blue-200'
                        } ${alert.source_url ? 'cursor-pointer hover:bg-opacity-80' : ''}`}
                        onClick={() => alert.source_url && handleAlertClick(alert)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-grow">
                            {getAlertIcon(alert.type)}
                            <div className="flex-grow">
                              <div className="flex items-start gap-2">
                                <div className="flex-1">
                                  <h3 className="font-semibold text-[#0A1A2F]">{alert.title}</h3>
                                  <p className="text-sm text-[#5C6B7A] mt-1">{alert.reason}</p>
                                </div>
                                {alert.source_url && (
                                  <ExternalLink className="w-4 h-4 text-[#5C6B7A] flex-shrink-0 mt-1" />
                                )}
                              </div>
                              <div className="flex items-center gap-4 mt-2 text-xs text-[#5C6B7A]">
                                <span>{format(new Date(alert.created_at), 'MMM d, HH:mm')}</span>
                                {alert.entity_name && alert.entity_name !== "" && (
                                  <span className="capitalize">{alert.entity_name}</span>
                                )}
                                {alert.delta !== undefined && alert.delta !== null && (
                                  <Badge variant="outline" className="text-xs">
                                    {alert.delta > 0 ? '+' : ''}{alert.delta}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => deleteAlert(alert.id, e)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span className="sr-only">Delete alert</span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <div className="space-y-6">
              {/* Live Deal Alerts */}
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <ShoppingCart className="w-5 h-5 text-blue-500" />
                    <div>
                      <CardTitle>Live Deal Alerts</CardTitle>
                      <CardDescription>Get notified about new procurement opportunities</CardDescription>
                    </div>
                    <Switch
                      checked={preferences.live_deal_on}
                      onCheckedChange={(checked) => 
                        savePreferences({ ...preferences, live_deal_on: checked })
                      }
                      disabled={isSaving}
                    />
                  </div>
                </CardHeader>
                {preferences.live_deal_on && (
                  <CardContent>
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Categories to watch</Label>
                      <div
                        className="grid grid-cols-1 md:grid-cols-2 gap-2"
                        style={{
                          gridAutoFlow: 'column',
                          gridTemplateRows: `repeat(${categoryGridRows}, auto)`,
                        }}
                      >
                        {SORTED_CATEGORY_OPTIONS.map(category => (
                          <Label key={category} className="flex items-center space-x-2 text-sm">
                            <input
                              type="checkbox"
                              checked={preferences.live_deal_categories?.includes(category) || false}
                              onChange={(e) => {
                                const categories = preferences.live_deal_categories || [];
                                const updated = e.target.checked
                                  ? [...categories, category]
                                  : categories.filter(c => c !== category);
                                savePreferences({ ...preferences, live_deal_categories: updated });
                              }}
                              disabled={isSaving}
                            />
                            <span>{category}</span>
                          </Label>
                        ))}
                      </div>
                      {preferences.live_deal_categories?.length === 0 && (
                        <p className="text-xs text-[#5C6B7A] mt-2">
                          No categories selected — live deal email alerts will not be sent. Select at least one category to watch.
                        </p>
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>

              {showCore && (
              <>
              {/* Momentum Alerts */}
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-5 h-5 text-green-500" />
                      <div>
                        <CardTitle>Momentum Alerts</CardTitle>
                        <CardDescription>Get notified about significant account momentum changes</CardDescription>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.momentum_on}
                      onCheckedChange={(checked) => 
                        savePreferences({ ...preferences, momentum_on: checked })
                      }
                      disabled={isSaving}
                    />
                  </div>
                </CardHeader>
                {preferences.momentum_on && (
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <Label htmlFor="momentum-threshold" className="text-sm font-medium">
                        Alert threshold:
                      </Label>
                      <Input
                        id="momentum-threshold"
                        type="number"
                        min="1"
                        max={MOMENTUM_THRESHOLD_MAX}
                        value={clampAlertThreshold(preferences.momentum_threshold, MOMENTUM_THRESHOLD_MAX)}
                        onChange={(e) =>
                          savePreferences({
                            ...preferences,
                            momentum_threshold: clampAlertThreshold(e.target.value, MOMENTUM_THRESHOLD_MAX),
                          })
                        }
                        className="w-20"
                        disabled={isSaving}
                      />
                      <span className="text-sm text-[#5C6B7A]">signal strength (0–6)</span>
                    </div>
                    <p className="text-xs text-[#5C6B7A] mt-2">
                      Alert me when an account's strongest momentum signal reaches this grade. 6 is the strongest recorded.
                    </p>
                  </CardContent>
                )}
              </Card>

              {/* Persona Alerts */}
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <UserCheck className="w-5 h-5 text-purple-500" />
                      <div>
                        <CardTitle>Persona Activity Alerts</CardTitle>
                        <CardDescription>Get notified about key persona activities and changes</CardDescription>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.persona_on}
                      onCheckedChange={(checked) => 
                        savePreferences({ ...preferences, persona_on: checked })
                      }
                      disabled={isSaving}
                    />
                  </div>
                </CardHeader>
                {preferences.persona_on && (
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <Label htmlFor="persona-threshold" className="text-sm font-medium">
                        Alert threshold:
                      </Label>
                      <Input
                        id="persona-threshold"
                        type="number"
                        min="1"
                        max={PERSONA_THRESHOLD_MAX}
                        value={clampAlertThreshold(preferences.persona_threshold, PERSONA_THRESHOLD_MAX)}
                        onChange={(e) =>
                          savePreferences({
                            ...preferences,
                            persona_threshold: clampAlertThreshold(e.target.value, PERSONA_THRESHOLD_MAX),
                          })
                        }
                        className="w-20"
                        disabled={isSaving}
                      />
                      <span className="text-sm text-[#5C6B7A]">signal score (0–5)</span>
                    </div>
                    <p className="text-xs text-[#5C6B7A] mt-2">
                      Alert me when a contact's activity score reaches this. 5 is the strongest recorded.
                    </p>
                  </CardContent>
                )}
              </Card>
              </>
              )}

              {/* Do Not Disturb */}
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-orange-500" />
                    <div>
                      <CardTitle>Do Not Disturb</CardTitle>
                      <CardDescription>Set quiet hours when you don't want to receive alerts</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="dnd-start" className="text-sm font-medium">From</Label>
                      <Input
                        id="dnd-start"
                        type="time"
                        step={60}
                        value={dndForm.start}
                        onChange={(e) => {
                          const next = { ...dndForm, start: e.target.value };
                          setDndForm(next);
                          scheduleDndSave(next);
                        }}
                        onBlur={() => {
                          const base = preferencesRef.current;
                          if (!base?.id || isLoading) return;
                          savePreferences(
                            { ...base, dnd_start: dndForm.start, dnd_end: dndForm.end, timezone: dndForm.timezone },
                            { silent: true }
                          );
                        }}
                        disabled={isLoading || !preferences?.id}
                      />
                    </div>
                    <div>
                      <Label htmlFor="dnd-end" className="text-sm font-medium">Until</Label>
                      <Input
                        id="dnd-end"
                        type="time"
                        step={60}
                        value={dndForm.end}
                        onChange={(e) => {
                          const next = { ...dndForm, end: e.target.value };
                          setDndForm(next);
                          scheduleDndSave(next);
                        }}
                        onBlur={() => {
                          const base = preferencesRef.current;
                          if (!base?.id || isLoading) return;
                          savePreferences(
                            { ...base, dnd_start: dndForm.start, dnd_end: dndForm.end, timezone: dndForm.timezone },
                            { silent: true }
                          );
                        }}
                        disabled={isLoading || !preferences?.id}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="timezone" className="text-sm font-medium">Timezone</Label>
                    <Select
                      value={dndForm.timezone}
                      onValueChange={(value) => {
                        const next = { ...dndForm, timezone: value };
                        setDndForm(next);
                        const base = preferencesRef.current;
                        if (base?.id) {
                          savePreferences(
                            { ...base, dnd_start: next.start, dnd_end: next.end, timezone: value },
                            { silent: true }
                          );
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select timezone" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Australia/Sydney">Australia/Sydney</SelectItem>
                        <SelectItem value="Australia/Melbourne">Australia/Melbourne</SelectItem>
                        <SelectItem value="Australia/Brisbane">Australia/Brisbane</SelectItem>
                        <SelectItem value="Australia/Perth">Australia/Perth</SelectItem>
                        <SelectItem value="America/New_York">America/New_York</SelectItem>
                        <SelectItem value="America/Los_Angeles">America/Los_Angeles</SelectItem>
                        <SelectItem value="Europe/London">Europe/London</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Email Digest Settings */}
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-[#00C2C2]" />
                      <div>
                        <CardTitle>Email Digest</CardTitle>
                        <CardDescription>Receive a daily email summary of your alerts</CardDescription>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.email_digest_on === true}
                      onCheckedChange={(checked) => 
                        savePreferences({ ...preferences, email_digest_on: checked })
                      }
                      disabled={isSaving}
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-[#5C6B7A]">
                    {preferences.email_digest_on === true 
                      ? "You'll receive a daily email when you have new alerts."
                      : "Email digests are turned off. You won't receive daily alert emails."}
                  </p>
                </CardContent>
              </Card>

              {/* [2026-11-01] Review reminders — untouched live deals with a written deadline that are a
                  Strong or Moderate fit. Sent only while email alerts are on; each tender appears in at
                  most two reminders (when it is new, and in its closing week). */}
              {myDealsEnabled && (
                <Card className="border-0 shadow-md">
                  <CardHeader>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-[#00C2C2]" />
                        <div>
                          <CardTitle>Review reminders</CardTitle>
                          <CardDescription>A reminder about live tenders that fit you and nobody has reviewed yet</CardDescription>
                        </div>
                      </div>
                      <Select
                        value={preferences.deal_review_reminder || 'weekly'}
                        onValueChange={(value) => savePreferences({ ...preferences, deal_review_reminder: value })}
                        disabled={isSaving || preferences.email_digest_on !== true}
                      >
                        <SelectTrigger className="w-44">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Weekly (Mondays)</SelectItem>
                          <SelectItem value="fortnightly">Every two weeks</SelectItem>
                          <SelectItem value="off">Off</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-[#5C6B7A]">
                      {preferences.email_digest_on !== true
                        ? 'Turn on the email digest above to receive review reminders.'
                        : (preferences.deal_review_reminder || 'weekly') === 'off'
                          ? "Review reminders are off. You won't be reminded about untouched deals."
                          : 'One email listing live tenders with a deadline that are a strong or moderate fit and have no status yet. Each tender appears at most twice: when it is new, and in the week it closes. Mark a deal in My Deals to stop reminders about it.'}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}